import React, { useState, useRef, useEffect } from 'react';
import './Login.scss';
import { getCookie } from '../utils/utils';
import TagManager from 'react-gtm-module';
import { Trans, useTranslation } from 'react-i18next';
import { trackStep } from '../utils/errorReporting';
import LoadingLabel from '../common/LoadingLabel';

type Props = {
  email: string;
  loginError: string;
  toggleSignupLoginModalVisible: () => void;
  onLoginSuccess: (expiresIn: number, marketingConsentShown: boolean) => void;
  setLoginError: (error: string) => void;
  validatePassword: (password: string) => string;
  onForgotPasswordClick: () => void; // Add this prop
  // Right password, unconfirmed account: the shell moves to the code step.
  onEmailNotConfirmed: () => void;
  // Tells the shell a login request is out, so a close meanwhile keeps what the resumed flow needs.
  onRequestInFlightChange: (inFlight: boolean) => void;
  setIsAdmin: (isAdmin: boolean) => void;
  setResponseEmail: (email: string) => void;
};

/**
 * Password step of the auth surface: the user's email as context, a password
 * field, the submit CTA and the only entry point to the forgot-password
 * screen. Reached from the email step once the lookup says the account exists.
 */
const Login = (props: Props) => {
  const {
    email,
    loginError,
    toggleSignupLoginModalVisible,
    onLoginSuccess,
    setLoginError,
    validatePassword,
    onForgotPasswordClick, // Destructure the new prop
    onEmailNotConfirmed,
    onRequestInFlightChange,
    setIsAdmin,
    setResponseEmail,
  } = props;
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [passwordError, setPasswordError] = useState(''); // Add this line

  // Add these references
  const loginPasswordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (loginPasswordInputRef.current) {
      loginPasswordInputRef.current.focus();
    }
  }, []);

  /**
   * Posts the credentials and closes the modal on success. The backend answers
   * email_not_confirmed only after the password checked out, so that case goes
   * to the code step rather than "Check password"; every other failure keeps it.
   */
  const handleSubmitLogin = async () => {
    setIsLoggingIn(true);
    onRequestInFlightChange(true);
    TagManager.dataLayer({
      dataLayer: {
        event: 'login_button_clicked',
        category: 'User Interaction',
        action: 'Click',
        label: 'Login Button',
      },
    });
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify({ email: email.toLowerCase(), password }),
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setLoginError(''); // Clear any previous error message
        onLoginSuccess(data.expires_in, data.marketing_consent_shown ?? true);
        setIsAdmin(data.is_admin);
        setResponseEmail(data.email);
        TagManager.dataLayer({
          dataLayer: {
            event: 'login_success',
            category: 'User Interaction',
            action: 'Success',
            label: 'Login Success',
            userId: email,
          },
        });
        trackStep('login-result', { result: 'success' });
        toggleSignupLoginModalVisible(); // Close the modal after successful login
      } else {
        // A proxy or crash can answer with a non-JSON body; that reads as a plain failure.
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'email_not_confirmed') {
          onEmailNotConfirmed();
          return;
        }
        console.error('Login failed');
        trackStep('login-result', { result: 'rejected', status: String(response.status) });
        setLoginError(t('signupLoginModal.checkPassword')); // Set error message
      }
    } catch (error) {
      console.error('Error:', error);
      trackStep('login-result', { result: 'error' });
      setLoginError(t('signupLoginModal.unexpectedError')); // Set error message for unexpected errors
    } finally {
      setIsLoggingIn(false);
      onRequestInFlightChange(false);
    }
  };

  const hasError = Boolean(passwordError || loginError);

  return (
    <div className="login-screen">
      <div className="login-screen__intro">
        <h2 className="login-screen__heading">
          <Trans i18nKey="signupLoginModal.login" />
        </h2>
      </div>
      <form
        className="login-screen__form"
        onSubmit={(e) => {
          e.preventDefault();
          const passwordValidationError = validatePassword(password);
          setPasswordError(passwordValidationError); // Validate password on submit
          if (!passwordValidationError) {
            handleSubmitLogin();
          }
        }}
      >
        <div className="login-screen__field">
          <input
            type="password"
            placeholder={t("signupLoginModal.passwordPlaceholder")}
            className={
              hasError
                ? 'login-screen__input login-screen__input--invalid'
                : 'login-screen__input'
            }
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            required
            ref={loginPasswordInputRef}
            autoComplete="current-password"
          />
          {passwordError && (
            <p className="login-screen__error">{passwordError}</p>
          )}
        </div>
        <div className="login-screen__field">
          {loginError && <p className="login-screen__error">{loginError}</p>}
          <button
            className={
              isLoggingIn
                ? 'login-screen__submit login-screen__submit--loading'
                : 'login-screen__submit'
            }
            disabled={isLoggingIn}
            type="submit"
          >
            <LoadingLabel loading={isLoggingIn}>
              <Trans i18nKey="signupLoginModal.loginButton" />
            </LoadingLabel>
          </button>
        </div>
      </form>
      <button
        type="button"
        onClick={onForgotPasswordClick}
        className="login-screen__forgot"
      >
        <Trans i18nKey="signupLoginModal.forgotPassword.linkText" />
      </button>
    </div>
  );
};

export default Login;
